// Bloc de To Do's: reglas de fechas, alertas, agenda y orden. Funciones puras, con pruebas (todo.test.ts).
import { addDias, diasEntre } from './fechas';

export interface HojaTodo { id: string; nombre: string; orden: number; archivada_en: string | null }
export interface PendienteTodo { id: string; hoja_id: string; texto: string; orden: number; hecho: boolean; fecha_seguimiento: string | null; archivado_en: string | null; pedido_id: string | null }

const DIAS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

export type EstadoFecha = 'vencida' | 'hoy' | 'manana' | 'futura';
export const estadoFecha = (f: string, hoy: string): EstadoFecha => {
  const n = diasEntre(hoy, f);
  return n < 0 ? 'vencida' : n === 0 ? 'hoy' : n === 1 ? 'manana' : 'futura';
};
// "Vencida hace 2 días", "Hoy", "Mañana", "jue 15 oct".
export function textoFecha(f: string, hoy: string): string {
  const n = diasEntre(hoy, f);
  if (n < 0) return `Vencida hace ${-n} ${n === -1 ? 'día' : 'días'}`;
  if (n === 0) return 'Hoy';
  if (n === 1) return 'Mañana';
  const d = new Date(f + 'T12:00:00');
  return `${DIAS[d.getDay()]} ${d.getDate()} ${MESES[d.getMonth()]}`;
}

// Un pendiente cuenta (para alertas y agenda) si no está hecho ni archivado y su hoja no está archivada.
export function activo(p: PendienteTodo, hojas: HojaTodo[]): boolean {
  if (p.hecho || p.archivado_en) return false;
  const h = hojas.find(x => x.id === p.hoja_id);
  return !!h && !h.archivada_en;
}

export interface FilaTodo { id: string; texto: string; hojaId: string; hoja: string; fecha: string; dias: number; pedido_id: string | null }
const fila = (p: PendienteTodo, hojas: HojaTodo[], hoy: string): FilaTodo => {
  const h = hojas.find(x => x.id === p.hoja_id)!;
  return { id: p.id, texto: p.texto, hojaId: h.id, hoja: h.nombre, fecha: p.fecha_seguimiento!, dias: diasEntre(hoy, p.fecha_seguimiento!), pedido_id: p.pedido_id };
};
const porFecha = (a: FilaTodo, b: FilaTodo) => a.fecha.localeCompare(b.fecha) || a.hoja.localeCompare(b.hoja) || a.texto.localeCompare(b.texto);
// Con fecha, activos, ordenados por fecha.
const conFecha = (ps: PendienteTodo[], hojas: HojaTodo[], hoy: string) => ps.filter(p => p.fecha_seguimiento && activo(p, hojas)).map(p => fila(p, hojas, hoy)).sort(porFecha);

// Alerta: la fecha de seguimiento llegó (hoy) o ya pasó.
export const alertasTodo = (ps: PendienteTodo[], hojas: HojaTodo[], hoy: string) => conFecha(ps, hojas, hoy).filter(f => f.dias <= 0);
export const paraManana = (ps: PendienteTodo[], hojas: HojaTodo[], hoy: string) => conFecha(ps, hojas, hoy).filter(f => f.dias === 1);
// Alertas por hoja, para el punto rojo de cada pestaña.
export function alertasPorHoja(ps: PendienteTodo[], hojas: HojaTodo[], hoy: string): Record<string, number> {
  const r: Record<string, number> = {};
  for (const f of alertasTodo(ps, hojas, hoy)) r[f.hojaId] = (r[f.hojaId] || 0) + 1;
  return r;
}

// Posponer: la nueva fecha cuenta desde hoy (un seguimiento vencido hace tres días pospuesto un día queda para mañana).
export const posponerA = (hoy: string, dias: number) => addDias(hoy, dias);

export type GrupoAgenda = 'vencidas' | 'hoy' | 'manana' | 'semana' | 'despues';
export const TITULO_GRUPO: Record<GrupoAgenda, string> = { vencidas: 'Vencidas', hoy: 'Hoy', manana: 'Mañana', semana: 'Próximos 7 días', despues: 'Más adelante' };
export function grupoDe(dias: number): GrupoAgenda { return dias < 0 ? 'vencidas' : dias === 0 ? 'hoy' : dias === 1 ? 'manana' : dias <= 7 ? 'semana' : 'despues'; }
// Todos los pendientes con fecha de las hojas no archivadas, agrupados por cercanía. Los grupos vacíos no salen.
export function agenda(ps: PendienteTodo[], hojas: HojaTodo[], hoy: string): { grupo: GrupoAgenda; titulo: string; filas: FilaTodo[] }[] {
  const por = new Map<GrupoAgenda, FilaTodo[]>();
  for (const f of conFecha(ps, hojas, hoy)) por.set(grupoDe(f.dias), [...(por.get(grupoDe(f.dias)) || []), f]);
  return (['vencidas', 'hoy', 'manana', 'semana', 'despues'] as GrupoAgenda[]).filter(g => por.has(g)).map(g => ({ grupo: g, titulo: TITULO_GRUPO[g], filas: por.get(g)! }));
}

// Orden entre vecinos: un número entre `antes` y `despues` (null = no hay vecino de ese lado).
export function ordenEntre(antes: number | null, despues: number | null): number {
  if (antes == null && despues == null) return 1;
  if (antes == null) return despues! - 1;
  if (despues == null) return antes + 1;
  return (antes + despues) / 2;
}
export const ordenar = <T extends { orden: number; id: string }>(xs: T[]) => [...xs].sort((a, b) => a.orden - b.orden || a.id.localeCompare(b.id));

// Pendientes que se crean desde las alertas del Tablero: un texto por pedido.
const TEXTOS: Record<string, (id: string) => string> = {
  direccion: id => `Confirmar la dirección de despacho del ${id}`,
  atrasados: id => `Revisar el envío atrasado del ${id}`,
  verificar: id => `Verificar la transferencia del ${id}`,
  cobrar: id => `Cobrar el saldo del ${id}`,
  vencidas: id => `Hacer seguimiento a la cotización ${id}`,
};
export const alertaConTodo = (alertaId: string) => alertaId in TEXTOS;
// Los que faltan por crear: se salta el pedido que ya tiene un pendiente igual sin hacer y sin archivar (así el botón no duplica).
export function nuevosDeAlerta(alertaId: string, pedidos: string[], existentes: PendienteTodo[]): { texto: string; pedido_id: string }[] {
  const t = TEXTOS[alertaId];
  if (!t) return [];
  return [...new Set(pedidos)].map(id => ({ texto: t(id), pedido_id: id }))
    .filter(n => !existentes.some(e => e.pedido_id === n.pedido_id && e.texto === n.texto && !e.hecho && !e.archivado_en));
}
